package com.neurobs.js.dom

import org.scalajs.dom.html
import org.scalajs.dom.html.Span
import org.scalajs.dom.raw._
import scalatags.JsDom.all._
import scalatags.JsDom.{all => stags}
import com.neurobs.js.view
import com.neurobs.js.view.{Form, MessageBoxResult}
import com.neurobs.util.Guid
import com.neurobs.js.dom.Grid._
import com.neurobs.js.util.RandomString

object ElementFactory {
	import scala.language.implicitConversions
	import DOMViewElement._

	val defaultGridSpacingEm = 1;

	private val defaultTextInputClass = "efactory-text-input"
	private val defaultPasswordInputClass = "efactory-password-input"
	private val defaultTextClass = "efactory-text"
	private val defaultCheckboxClass = "efactory-checkbox"
	private val defaultButtonClass = "efactory-button"
	private val defaultLinkClass = "efactory-link"
	private val defaultTableClass = "efactory-table"
	private val defaultTableHeaderRowClass = "efactory-table-header-row"
	private val defaultSingleColumnTableRowClass = "efactory-table-single-column-row"
	private val defaultTableRowClass = "efactory-table-row"
	private val defaultSelectClass = "efactory-select"
	private val defaultRadioClass = "efactory-radio"

	def makeLink(text: String, `class`: Option[String] = None): html.Anchor = a(
		stags.`class` := `class`.getOrElse(defaultLinkClass)
	)(text).render

	def makeExternalLink(text: String, url: String, newTab: Boolean = false): html.Anchor = a(href := url, target := (if (newTab) "_blank" else "_self"), text).render

	def makeTextInput(width: Int, initialValue: String = "", `class`: Option[String] = None): html.Input = input(
		`type` := "text",
		stags.`class` := `class`.getOrElse(defaultTextInputClass),
		style := s"width:${width}em",
		value := initialValue
	).render

	def makeRadio(inputName: String, inputValue: String, checked: Boolean = false): html.Input = {
		val i = input(`type` := "radio", name := inputName, value := inputValue, stags.`class` := defaultRadioClass).render
		i.checked = checked
		i
	}

	def makePasswordInput(width: Int, initialValue: String = "", `class`: Option[String] = None): html.Input = input(
		`type` := "password",
		stags.`class` := `class`.getOrElse(defaultPasswordInputClass),
		style := s"width:${width}em",
		value := initialValue
	).render

	def makeText(text: String = "", `class`: Option[String] = None): html.Span = span(
		stags.`class` := `class`.getOrElse(defaultTextClass)
	)(text).render

	def makeCheckbox(checked: Boolean = false, `class`: Option[String] = None): html.Input = {
		val i = input(
			`type` := "checkbox",
			stags.`class` := `class`.getOrElse(defaultCheckboxClass)
		).render
		i.setChecked(checked)
		i
	}

	def makeButton(caption: String, `class`: Option[String] = None): html.Button = button(
		stags.`class` := `class`.getOrElse(defaultButtonClass)
	)(caption).render

	def makeSelectBox(width: Int, heightSize: Int = 1, `class`: Option[String] = None): html.Select = select(
		stags.`class` := `class`.getOrElse(defaultSelectClass),
		style := s"width:${width}em",
		size := heightSize
	).render

	def makeTable(headers: Seq[HTMLElement], `class`: Option[String] = None): html.Table = {
		val r = tr(stags.`class` := defaultTableHeaderRowClass).render
		for (e <- headers) r.appendChild(td(e).render)
		table(stags.`class` := `class`.getOrElse(defaultTableClass))(r).render
	}

	def makeHeaderlessTable(`class`: Option[String] = None): html.Table = {
		table(stags.`class` := `class`.getOrElse(defaultTableClass)).render
	}

	def makeTableRow(columns: Seq[HTMLElement], `class`: Option[String] = None): html.TableRow = {
		val r = tr(stags.`class` := `class`.getOrElse(defaultTableRowClass)).render
		for (c <- columns) r.appendChild(td(c).render)
		r
	}

	def makeSingleColumnTableRow(cell: HTMLElement, columnSpan: Int, `class`: Option[String] = None): html.TableRow = {
		tr(stags.`class` := `class`.getOrElse(defaultSingleColumnTableRowClass), td(cell, colspan := columnSpan)).render
	}

	def toElement(c: view.Container): HTMLElement = c match {
		case ElementContainer(e) => e
		case _ => throw new Exception("ElementFactory.toElement invalid Container")
	}

	class RefreshLink extends view.Link {
		private val inactive = span(style := "display:none;")(" (Updating...)").render
		private val link = AnchorLink(makeLink(" (Refresh)"))
		private val active = span(link.anchor).render
		val element: HTMLElement = span(inactive, active).render

		override def setHandler(handler: () => Unit): RefreshLink = {
			link.setHandler(handler)
			this
		}
		override def setActive(active: Boolean): Unit = {
			inactive.style.display = if (active) "none" else "inline-block"
			this.active.style.display = if (active) "inline-block" else "none"
		}
		override def setHref(url: String): Unit = link.setHref(url)
		override def setText(t: String): Unit = link.setText(t)
		override def getText(): String = link.getText()
	}

	class FileSelectSpan extends view.FileSelectBox {
		private val fileInput = input(style := "display:none;", `type` := "file", id := RandomString.generate(length = 16)).render

		val element: HTMLElement = {
			val button = label(`for` := fileInput.id, `class` := "structure-file-label", "Select File").render
			val filenameBox = makeTextInput(20)
			filenameBox.disabled = true
			fileInput.onchange = (_: Event) => {
				filenameBox.value = if (fileInput.files.length == 1) fileInput.files.item(0).name else ""
			}
			span(filenameBox, raw("&nbsp;"), button, fileInput).render
		}

		override def value: Option[org.scalajs.dom.raw.File] = if (fileInput.files.length == 1) Some(fileInput.files.item(0)) else None
	}

	private val formTableClass = "structure-form-table"
	private val formTableLeftColumnClass = "structure-form-table-left-column"
	private val formTableRightColumnClass = "structure-form-table-right-column"

	def makeFormTable(elements: Array[(HTMLElement, HTMLElement)]) : HTMLElement = {
		val tab = table(`class` := formTableClass).render
		for (row <- elements) {
			tab.appendChild(tr(td(`class` := formTableLeftColumnClass)(row._1), td(`class` := formTableRightColumnClass)(row._2)).render)
		}
		tab
	}

	private val hideableRowFormTableLeftClass = "structure-form-hideable-row-left"
	private val hideableRowFormTableRightClass = "structure-form-hideable-row-right"

	class HideableRowFormTable {
		case class RowId (private[HideableRowFormTable] val element: HTMLElement)

		private val grid = new HideableRowGrid(fullWidth = false, emGapSize = 0.7, alignItems = Grid.alignItemsCenter)

		def addRow(left: HTMLElement, right: HTMLElement): RowId = {
			val leftWrapper = div(`class` := hideableRowFormTableLeftClass, left).render
			val rightWrapper = div(`class` := hideableRowFormTableRightClass, right).render
			grid.addRow(Array[GridCell]((leftWrapper, Grid.alignRight), (rightWrapper, Grid.alignLeft)))
			RowId(leftWrapper)
		}
		def setRowVisible(id: RowId, visible: Boolean): Unit = grid.setRowVisible(id.element, visible)
		def removeRow(id: RowId): Unit = grid.removeRow(id.element)
		def container: HTMLElement = grid.container
	}

	private val parameterTableClass = "structure-parameter-table"
	private val parameterTableBorderCellClass = "structure-parameter-table-border-cell"
	private val parameterTableNoBorderCellClass = "structure-parameter-table-no-border-cell"

	def makeParameterTable(elements: Array[(HTMLElement, HTMLElement)], useBorders: Boolean = false): HTMLElement = {
		val cellClass = if (useBorders) parameterTableBorderCellClass else parameterTableNoBorderCellClass

		val tab = table(`class` := parameterTableClass).render
		for (row <- elements) {
			tab.appendChild(tr(td(`class` := cellClass)(row._1), td(`class` := cellClass)(row._2)).render)
		}
		tab
	}

	private val panelViewMainClass = "structure-panel-main"
	private val panelViewTitleClass = "structure-panel-title"
	private val panelViewHeaderClass = "structure-panel-header"

	class PanelView (title: String, showRefresh: Boolean, fullWidth: Boolean, styles: Option[Seq[(String, String)]] = None, useHeader: Boolean = true) {
		private val leftCorner = spaceSpan
		def setLeftCorner(e: HTMLElement): Unit = {
			leftCorner.innerHTML = ""
			leftCorner.appendChild(e)
		}
		private val rightCorner = spaceSpan
		def setRightCorner(e: HTMLElement): Unit = {
			rightCorner.innerHTML = ""
			rightCorner.appendChild(e)
		}
		private val domTitleText = makeText(title)
		domTitleText.style.whiteSpace = "nowrap"
		val titleText: view.Text = domTitleText
		val refresh: RefreshLink = new RefreshLink
		val grid: Grid = new Grid(fullWidth, defaultGridSpacingEm)
		val container: HTMLElement = {
			val header = span(span(`class` := panelViewTitleClass, domTitleText)).render
			if (showRefresh) header.appendChild(span(raw("&nbsp;"),refresh.element).render)
			val mainDiv = div(
				`class` := panelViewMainClass,
				style := (if (fullWidth) "display: block;" else "display: inline-block;")
			).render
			if (useHeader) {
				mainDiv.appendChild(div(`class` := panelViewHeaderClass, makeLeftCenterRight(left = leftCorner, center = header, right = rightCorner)).render)
			}
			mainDiv.appendChild(grid.container)
			styles.foreach { props =>
				for (p <- props) mainDiv.style.setProperty(p._1, p._2)
			}
			mainDiv
		}
	}

	def spaceSpan: html.Span = span(raw("&nbsp;")).render

	def makeLeftCenterRight(left: HTMLElement = spaceSpan, center: HTMLElement = span().render, right: HTMLElement = spaceSpan): HTMLElement = {
		div(
			div(
				span(style := "float: left; text-align:left; width:20%;", left),
				span(style := "float: left; text-align:center; width:60%;", center),
				span(style := "float: left; text-align:right; width:20%;", right)
			),
			div(style := "clear: both;")
		).render
	}


	private def escapeForHTML(s: String): String = {
		div(s).render.innerHTML.replace("\n", "<br>")
	}

	private val formButtonRowClass = "structure-form-button-row"
	private val formTitleClass = "structure-form-title"
	private val statusErrorClass = "structure-status-error"
	private val statusProgressClass = "structure-status-progress"

	def makeForm(width: Int, title: String, description: String, elements: Array[(HTMLElement, HTMLElement)], useCancel: Boolean, submitCaption: String, extraContent: scala.Option[HTMLElement] = None) : view.Form = {
		val submitButton = makeButton(submitCaption)
		val cancelButton = if (useCancel) Some(makeButton("Cancel")) else None
		val buttonRow = div(`class` := formButtonRowClass)(submitButton).render
		if (cancelButton.isDefined) {
			buttonRow.appendChild(div(style := "width: 1em;display: inline-block;").render)
			buttonRow.appendChild(cancelButton.render)
		}
		val grid = new HideableRowGrid(fullWidth = false, emGapSize = 1, defaultAlign = Grid.alignCenter)
		grid.container.style.width = s"${width}em"
		grid.addRow(div(`class` := formTitleClass)(title).render)
		if (!description.isEmpty) {
			grid.addRow(div(description).render)
		}
		if (!elements.isEmpty) {
			grid.addRow(makeFormTable(elements))
		}
		extraContent.foreach(c => grid.addRow(c))

		val status = makeText()
		grid.addRow(status)
		grid.setRowVisible(status, visible = false)
		val statusImpl = new Form.Status {
			override def setValue(value: String, t: Form.StatusType.Value): Unit =  {
				if (value.isEmpty) grid.setRowVisible(status, visible = false)
				else {
					status.innerHTML = escapeForHTML(value)
					status.className = t match {
						case view.Form.StatusType.Error => statusErrorClass
						case view.Form.StatusType.Progress => statusProgressClass
						case _ => ""
					}
					grid.setRowVisible(status, visible = true)
				}
			}
		}

		grid.addRow(buttonRow)
		view.Form(grid.container, submitButton, cancelButton.map(ButtonButton(_)), statusImpl)
	}

	private val dialogTitleClass = "structure-dialog-title-container"
	private val dialogDescriptionClass = "structure-dialog-description"
	private var dialogCustomContentClass = "structure-dialog-custom-content-container"

	def makeSimpleMessageDialog(width: Int, title: String, description: String, dismissButtonCaption: String, customContent: scala.Option[HTMLElement] = None) : view.MessageBox = {
		makeMessageDialog(width, title, description, List((dismissButtonCaption, MessageBoxResult.Ok)), customContent)
	}

	def makeMessageDialog(width: Int, title: String, description: String, buttonCaptions: Seq[(String, MessageBoxResult.Value)], customContent: scala.Option[HTMLElement] = None) : view.MessageBox = {
		val buttons = buttonCaptions.map(b => (ButtonButton(makeButton(b._1)), b._2))
		val buttonRow = div(`class` := formButtonRowClass).render
		var first = true
		buttons.foreach(b => {
			if (!first) buttonRow.appendChild(div(style := "width: 1em;display: inline-block;").render)
			buttonRow.appendChild(b._1.button)
			first = false
		})
		val form =
			div(style := (if (width > 0) s"width:${width}em;" else ""))().render
		if (!title.isEmpty) {
			form.appendChild(
				div(`class` := dialogTitleClass)( makeText(title) ).render
			)
		}
		form.appendChild(
			div(`class` := dialogDescriptionClass)(description).render
		)
		if (customContent.isDefined) form.appendChild(div(`class` := dialogCustomContentClass)(customContent.get).render)
		form.appendChild(buttonRow)
		view.MessageBox(form, buttons)
	}

	def addTooltip(element: HTMLElement, toolTip: String) : Span = span(element, div(`class` := "tooltip")(" \u24D8", span(`class` := "tooltip-text-wrapper")(span(`class` := "tooltip-text")(toolTip)))).render

	def addTooltip(text: String, toolTip: String) : Span = addTooltip(span(text).render, toolTip)

	implicit def stringToElement(s: String): HTMLElement = makeText(s)

	def setDivVisible(visible: Boolean, e: HTMLElement, display: String = "block"): Unit = {
		e.style.display = if (visible) display else "none"
	}
	def setRowVisible(visible: Boolean, r: html.TableRow): Unit = {
		r.style.display = if (visible) "table-row" else "none"
	}
}
